<script setup lang="ts">
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import UploadState from './UploadState.vue';

import UploadCloud from '@/icons/line/upload-cloud.svg';

const props = defineProps<{
  accept?: string[];
  disabled?: boolean;
  error?: string;
  processing?: boolean;
  successful?: boolean;
  multiple?: boolean;
  maxFileSize?: number; // in MB
}>();

const [isDraggingOver, setIsDraggingOver] = useToggle(false);
const model = defineModel<File | File[] | undefined>();
const emit = defineEmits<{ error: [string] }>();

const invalidFileNames = ref<string[]>([]);

const fileDialog = useFileDialog();

const maxFileSizeMB = computed(() => props.maxFileSize ?? 10);
const maxFileSizeBytes = computed(() => maxFileSizeMB.value * 1024 * 1024);

const state = computed(() => {
  if (props.error) {
    return 'error';
  }
  if (props.processing) {
    return 'loading';
  }
  if (props.successful) {
    return 'success';
  }
  return 'success';
});

function validateAndSetFiles(files: File[]) {
  if (!files.length) {
    model.value = undefined;
    invalidFileNames.value = [];
    return;
  }

  const oversizedFiles = files.filter((file) => file.size > maxFileSizeBytes.value);
  if (oversizedFiles.length) {
    const fileNames = oversizedFiles.map((f) => f.name).join(', ');
    emit(
      'error',
      `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${fileNames}. Maximum size is ${
        maxFileSizeMB.value
      }MB.`
    );
    return;
  }

  if (props.multiple) {
    const existingFiles = Array.isArray(model.value) ? model.value : [];
    model.value = [...existingFiles, ...files];
  } else {
    model.value = files[0];
  }

  const invalidFiles = files.filter((file) => {
    if (!props.accept) {
      return false;
    }
    const extension = file.name.slice(file.name.lastIndexOf('.'));
    return !props.accept.includes(extension);
  });

  invalidFileNames.value = invalidFiles.map((f) => f.name);

  if (invalidFiles.length) {
    const fileNames = invalidFiles.map((f) => f.name).join(', ');
    emit('error', `Unsupported file type${invalidFiles.length > 1 ? 's' : ''}: ${fileNames}`);
  }
}

function openFileDialog() {
  if (props.disabled) {
    return;
  }
  fileDialog.open({
    accept: props.accept?.join(','),
    multiple: props.multiple
  });
  fileDialog.onChange((files) => {
    if (!files) {
      return;
    }
    validateAndSetFiles(Array.from(files));
  });
}

function dropHandler(ev: DragEvent) {
  setIsDraggingOver(false);
  if (props.disabled) {
    return;
  }

  const items = Array.from(ev.dataTransfer?.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile());

  const files = (items.length ? items : Array.from(ev.dataTransfer?.files ?? [])).filter(
    (f): f is File => !!f
  );

  validateAndSetFiles(files);
}

function dragLeaveHandler(ev: DragEvent) {
  if (
    ev.currentTarget instanceof HTMLElement &&
    ev.relatedTarget instanceof HTMLElement &&
    ev.currentTarget.contains(ev.relatedTarget)
  ) {
    return;
  }
  setIsDraggingOver(false);
}

watch(model, (v) => {
  if (!v) {
    fileDialog.reset();
  }
});
</script>

<template>
  <div class="flex flex-col gap-y-2.5">
    <template v-if="multiple || !modelValue">
      <div
        :class="[
          'hidden max-h-64 flex-col gap-4 rounded-lg border border-dashed px-10 pb-5 pt-6 text-center md:flex',
          {
            'border-zinc-300 bg-white': !disabled && !isDraggingOver,
            'focus-within:border-slate-400 focus-within:shadow-slate-100 focus-within:drop-shadow ':
              !disabled,
            'border-slate-200 bg-slate-50': disabled,
            'border-primary-400 bg-slate-50': isDraggingOver
          }
        ]"
        @drop.prevent="dropHandler"
        @dragover.prevent
        @dragenter="setIsDraggingOver(true)"
        @dragleave="dragLeaveHandler"
        @dragend="setIsDraggingOver(false)"
      >
        <div class="flex flex-col items-center gap-3">
          <UploadCloud class="h-7 text-slate-500" />
          <div class="flex flex-col items-center gap-1">
            <p class="text-base font-medium leading-4 text-zinc-700">
              Drag and drop {{ multiple ? 'files' : 'a file' }} to upload
            </p>
            <p class="text-sm leading-4 text-zinc-600">
              {{ accept ? accept.sort().join(', ') : 'Any file type' }} up to {{ maxFileSizeMB }}MB
            </p>
          </div>
        </div>

        <div>
          <Button
            :color="ButtonColor.slate"
            :disabled="disabled"
            :size="ButtonSize.sm"
            :variant="ButtonVariant.outlined"
            type="button"
            @click="openFileDialog"
            >Browse for {{ multiple ? 'files' : 'a file' }}</Button
          >
        </div>
      </div>
      <Button
        :color="ButtonColor.slate"
        :disabled="disabled"
        :iconLeft="UploadCloud"
        :size="ButtonSize.lg"
        :variant="ButtonVariant.outlined"
        class="w-full md:hidden"
        type="button"
        @click="openFileDialog"
        >Browse for {{ multiple ? 'files' : 'a file' }}</Button
      >
    </template>

    <UploadState
      v-if="modelValue"
      :modelValue="modelValue"
      @update:modelValue="model = $event"
      :state="state"
      :accept="accept"
      :error="error"
      :multiple="multiple"
      :invalidFileNames="invalidFileNames"
    />
  </div>
</template>
